import React, { useState, useCallback } from "react";
import { BigFormModal } from "@/components/ui/big-form-modal";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronRight, Save, Clock, Globe, BarChart3, CheckCircle, Eye, Info, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { HtmlWrapperPattern, HtmlWrapperInferenceMetrics } from "@/services/htmlWrapperInferenceService";
import { PatternPreviewModal } from "./PatternPreviewModal";
import { previewPatternOnUrl, PatternPreviewResult } from "@/services/patternPreviewService";

interface HtmlWrapperPatternsModalProps {
  isOpen: boolean;
  onClose: () => void;
  patterns: HtmlWrapperPattern[];
  fetchError: string | null;
  metrics?: HtmlWrapperInferenceMetrics;
  organizationId: string;
  locationId: string;
  isFetching: boolean;
  // NEW: selection state and handlers from hook
  selectedPatterns: Set<number>;
  togglePatternSelection: (index: number) => void;
  selectAllPatterns: () => void;
  clearSelection: () => void;
  onSavePatterns: () => Promise<void>;
  isSaving: boolean;
  context?: 'programs' | 'monitoring'; // NEW
  // Remove savedPatterns and removePattern
}

export const HtmlWrapperPatternsModal: React.FC<HtmlWrapperPatternsModalProps> = ({
  isOpen,
  onClose,
  patterns,
  fetchError,
  metrics,
  onSavePatterns,
  organizationId,
  locationId,
  isFetching,
  selectedPatterns,
  togglePatternSelection,
  selectAllPatterns,
  clearSelection,
  isSaving,
  context = 'programs', // default
  // Remove savedPatterns and removePattern
}) => {
  const [isTechnicalSummaryOpen, setIsTechnicalSummaryOpen] = useState(false);
  const [previewState, setPreviewState] = useState<{
    isOpen: boolean;
    isFetching: boolean;
    filteredHtml: string;
    originalHtml: string;
    error: string | null;
  }>({
    isOpen: false,
    isFetching: false,
    filteredHtml: '',
    originalHtml: '',
    error: null
  });

  // Remove all local selection state!

  const handlePreviewPattern = useCallback(async (url: string, pattern: HtmlWrapperPattern) => {
    setPreviewState(prev => ({ ...prev, isFetching: true, error: null }));
    try {
      const result = await previewPatternOnUrl(
        url,
        organizationId,
        locationId,
        pattern.pattern,
        pattern.description
      );

      if (result.success) {
        setPreviewState(prev => ({
          ...prev,
          isOpen: true,
          isFetching: false,
          filteredHtml: result.processedHtml || '',
          originalHtml: result.originalHtml || '',
          error: null
        }));
      } else {
        setPreviewState(prev => ({
          ...prev,
          isFetching: false,
          error: result.message || 'Failed to preview pattern'
        }));
        toast.error(result.message || 'Failed to preview pattern');
      }
    } catch (error) {
      console.error('[HtmlWrapperPatternsModal] Error previewing pattern:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      setPreviewState(prev => ({
        ...prev,
        isFetching: false,
        error: errorMessage
      }));
      toast.error(errorMessage);
    }
  }, [organizationId, locationId]);

  const formatTime = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'text-green-600';
    if (confidence >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getConfidenceLabel = (confidence: number) => {
    if (confidence >= 0.8) return 'High';
    if (confidence >= 0.6) return 'Medium';
    return 'Low';
  };

  const handleSave = async () => {
    await onSavePatterns();
    onClose(); // Close modal after save
  };

  // Context-specific title and help text
  const modalTitle = context === 'monitoring' ? 'Monitoring wrapper patterns' : 'HTML Wrapper Patterns';
  const helpText = context === 'monitoring'
    ? 'These patterns will be used to extract content for monitoring changes on your URLs.'
    : 'These patterns will be used to extract program content from your URLs.';

  return (
    <>
      <BigFormModal
        isOpen={isOpen}
        onClose={onClose}
        title={modalTitle}
        onSave={handleSave}
        isSaving={isSaving}
      >
        <div className="space-y-6">
          <div className="text-sm text-gray-600 mb-2">{helpText}</div>
          {/* Loading State */}
          {isFetching && (
            <div className="flex items-center justify-center py-8">
              <div className="flex flex-col items-center gap-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="text-gray-600">Analyzing HTML patterns...</span>
                <span className="text-sm text-gray-500">This may take a few moments</span>
              </div>
            </div>
          )}

          {/* Error State */}
          {fetchError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-red-800 mb-2">
                <Globe className="w-5 h-5" />
                <span className="font-semibold">Analysis Error</span>
              </div>
              <p className="text-red-700">{fetchError}</p>
              <p className="text-sm text-red-600 mt-2">
                Please check that the program URLs are accessible and try again.
              </p>
            </div>
          )}

          {/* Success State - Pattern Results */}
          {!isFetching && !fetchError && patterns.length > 0 && (
            <>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium mb-4">Found Wrapper Patterns</h3>
                  <div className="flex gap-2">
                    <Button
                      onClick={selectAllPatterns}
                      variant="outline"
                      size="sm"
                    >
                      {selectedPatterns.size === patterns.length ? 'Deselect All' : 'Select All'}
                    </Button>
                    <Button
                      onClick={handleSave}
                      disabled={isSaving || selectedPatterns.size === 0}
                      className="flex items-center gap-2"
                    >
                      <Save className="w-4 h-4" />
                      {isSaving ? 'Saving...' : `Save Selected (${selectedPatterns.size})`}
                    </Button>
                  </div>
                </div>

                <div className="text-sm text-gray-600 mb-4 p-3 bg-gray-50 rounded-md">
                  <p className="font-medium mb-2">Metric definitions:</p>
                  <ol className="list-decimal pl-5 space-y-1">
                    <li><strong>Pattern Reliability</strong> — Measures how reliable the pattern is when used. (Database field: 'confidence')</li>
                    <li><strong>Extraction Quality</strong> — Measures how much noise is removed from the raw HTML. (Database field: 'avgContentScore')</li>
                  </ol>
                </div>

                <div className="space-y-3">
                  {patterns.map((pattern, index) => (
                    <div key={index} className="border rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={selectedPatterns.has(index)}
                          onChange={() => togglePatternSelection(index)}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <h4 
                              className="font-medium cursor-pointer hover:text-blue-600 transition-colors"
                              onClick={() => togglePatternSelection(index)}
                            >
                              {pattern.description}
                            </h4>
                            <div className="flex items-center gap-3">
                              <span className={`text-sm font-medium ${getConfidenceColor(pattern.confidence)}`}>
                                {getConfidenceLabel(pattern.confidence)} ({(pattern.confidence * 100).toFixed(0)}%)
                              </span>
                              <span className="text-sm text-purple-600 font-medium">
                                {pattern.pattern.startsWith('EXCLUDE:') 
                                  ? `${(pattern.avgContentScore * 100).toFixed(1)}% removed`
                                  : `${(pattern.avgContentScore * 100).toFixed(1)}% extracted`
                                }
                              </span>
                              <span className="text-sm text-gray-500">
                                {pattern.urlsMatched.length} URLs matched
                              </span>
                            </div>
                          </div>
                          
                          <div className="bg-gray-50 p-3 rounded text-sm font-mono break-all">
                            {pattern.pattern}
                          </div>

                          {pattern.urlsMatched.length > 0 && (
                            <Collapsible>
                              <CollapsibleTrigger asChild>
                                <Button variant="ghost" size="sm" className="mt-2 p-0 h-auto">
                                  <span className="flex items-center gap-1">
                                    <ChevronRight className="w-4 h-4" />
                                    View matched URLs ({pattern.urlsMatched.length})
                                  </span>
                                </Button>
                              </CollapsibleTrigger>
                              <CollapsibleContent className="mt-2">
                                <div className="bg-blue-50 p-3 rounded text-sm">
                                  <ul className="space-y-1">
                                    {pattern.urlsMatched.map((url, urlIndex) => (
                                      <li key={urlIndex} className="flex items-center gap-2">
                                        <button
                                          onClick={() => handlePreviewPattern(url, pattern)}
                                          className="font-mono text-xs break-all text-blue-600 hover:text-blue-800 hover:underline flex-1 text-left"
                                        >
                                          {url}
                                        </button>
                                        <Button
                                          onClick={() => handlePreviewPattern(url, pattern)}
                                          variant="ghost"
                                          size="sm"
                                          className="p-1 h-auto"
                                          title="Preview pattern on this URL"
                                        >
                                          <Eye className="w-3 h-3" />
                                        </Button>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </CollapsibleContent>
                            </Collapsible>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Technical Summary - Collapsible */}
              {metrics && (
                <Collapsible open={isTechnicalSummaryOpen} onOpenChange={setIsTechnicalSummaryOpen}>
                  <CollapsibleTrigger asChild>
                    <Button variant="outline" className="w-full justify-between">
                      <span className="flex items-center gap-2">
                        <BarChart3 className="w-4 h-4" />
                        Technical Summary
                      </span>
                      {isTechnicalSummaryOpen ? 
                        <ChevronDown className="w-4 h-4" /> : 
                        <ChevronRight className="w-4 h-4" />
                      }
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-4">
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center gap-2">
                          <Globe className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-medium">Total URLs:</span>
                        </div>
                        <p className="text-sm">{formatNumber(metrics.totalUrls)}</p>

                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span className="text-sm font-medium">Successful Fetches:</span>
                        </div>
                        <p className="text-sm">{formatNumber(metrics.successfulFetches)}</p>

                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-orange-600" />
                          <span className="text-sm font-medium">Time Elapsed:</span>
                        </div>
                        <p className="text-sm">{formatTime(metrics.timeElapsedMs)}</p>

                        <div className="flex items-center gap-2">
                          <BarChart3 className="w-4 h-4 text-purple-600" />
                          <span className="text-sm font-medium">Average Page Size:</span>
                        </div>
                        <p className="text-sm">{formatNumber(metrics.avgPageSize)} chars</p>
                      </div>

                      {/* Success Rate */}
                      <div className="mt-4 pt-3 border-t border-gray-200">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Success Rate:</span>
                          <span className="text-sm font-semibold text-green-600">
                            {((metrics.successfulFetches / metrics.totalUrls) * 100).toFixed(1)}%
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Patterns Found:</span>
                          <span className="text-sm font-semibold text-blue-600">
                            {patterns.length}
                          </span>
                        </div>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}
            </>
          )}

          {/* No Patterns Found */}
          {!isFetching && !fetchError && patterns.length === 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-blue-800 mb-3">
                <Info className="w-5 h-5" />
                <span className="font-semibold">No Patterns Found</span>
              </div>
              <p className="text-blue-700">
                No HTML wrapper patterns were found in the program URLs.
              </p>
            </div>
          )}

          {/* Add saved patterns list for monitoring context */}
          {/* Remove saved patterns list for monitoring context from here */}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </BigFormModal>

      {/* Pattern Preview Modal */}
      <PatternPreviewModal
        isOpen={previewState.isOpen}
        onClose={() => setPreviewState(prev => ({ ...prev, isOpen: false }))}
        filteredHtml={previewState.filteredHtml}
        originalHtml={previewState.originalHtml}
        isFetching={previewState.isFetching}
        fetchError={previewState.error}
      />
    </>
  );
}; 